import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useDebounceCallback } from '@react-hook/debounce';
import { useTranslation } from "react-i18next";
import DOMPurify from 'dompurify';
import he from 'he';
import { Box, Button, Container, Drawer, Skeleton, Stack, Typography, Tooltip, useMediaQuery, useTheme } from '@mui/material';

import PrimaryButton from './../common/ui/qvshop/components/core/PrimaryButton';

import { notScrollable, flexColFullHeightScrollable, p1, m1 } from "../common/ui/sx/common";

import SpecificationsBox from '../common/ui/qvshop/components/SpecificationsBox';
import SubarticleSection from './components/SubarticleSection';
import ArticleReservationSection from '../common/ui/qvshop/components/ArticleReservationSection';
import ExtraArticles from './components/ExtraArticles';
import { usePageLayoutConfigurator } from '../common/hooks/usePageLayoutConfigurator';
import Breadcrumbs from '../common/ui/qvshop/components/Breadcrumbs';
import QVContainer from '../common/ui/qvshop/components/QVContainer';
import BackButton from '../common/ui/qvshop/components/core/BackButton';
import AvailabilityCalendar from '../qvshop/components/AvailabilityCalendar';
import SetComponentsBox from './components/SetComponentsBox';
import ProductImageGallery from './components/ProductImageGallery';

import { htmlDecode } from '../common/utils/HtmlDecode';
import { ShopDatesInterval } from '../common/utils/ShopDatesInterval';

import Debounce from '../common/utils/Debounce';
import PageNames from '../common/utils/PageNames';
import DateTimeFormatConfig from '../common/utils/DateTimeFormatConfig';
import { ValidationUtils } from '../common/utils/ValidationUtils';

import { checkAvailabilityForAccessories, checkAvailabilityForProduct, checkAvailabilityForSimilars, downloadAttachment, getAccessories, getProduct, getSimilars, resetStatusToIdle, selectAccessories, selectIsLoadingAccessories, selectIsLoadingProductAvailability, selectIsLoadingSimilars, selectLoadProductFailed, selectProduct, selectSimilars, selectTriggerProductAvailability, updateProduct } from '../common/store/productSlice';
import { selectAllArticlesMustBeInTheSameInterval, selectClosingDays, selectSemesterSettings, selectWorkingHours } from '../common/store/settingsSlice';
import { selectCategory, selectEndDate, selectStartDate, setEndDate, setStartDate } from '../common/store/filtersSlice';

export default function ProductDetailsView(props) {
    usePageLayoutConfigurator(PageNames.PRODUCT_DETAILS);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { productId } = useParams();
    const theme = useTheme();
    const showMobileBooking = useMediaQuery(theme.breakpoints.down('md'));
    const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false);

    const workingHours = useSelector(selectWorkingHours);
    const semesterSettings = useSelector(selectSemesterSettings);
    const closingDays = useSelector(selectClosingDays);

    const selectedCategory = useSelector(selectCategory);

    const startDate = useSelector(selectStartDate);
    const endDate = useSelector(selectEndDate);

    const allArticlesMustBeInASingleInterval = useSelector(selectAllArticlesMustBeInTheSameInterval);
    const product = useSelector(selectProduct);
    const loadProductFailed = useSelector(selectLoadProductFailed);
    const images = product && (product.images.length > 0 || (product.image && product.image !== '_dummy.svg')) ? [product.image, ...product.images] : [];

    const triggerAvailability = useSelector(selectTriggerProductAvailability);
    const loadingProductAvailability = useSelector(selectIsLoadingProductAvailability);

    const similarArticles = useSelector(selectSimilars);
    const loadingSimilars = useSelector(selectIsLoadingSimilars);
    const optionalArticles = useSelector(selectAccessories);
    const loadingAccessories = useSelector(selectIsLoadingAccessories);

    useEffect(() => {
        if (loadProductFailed) {
            // ANDY - 30.03.2023 - We also have the error here if we want to present it shomehow in the future
            navigate("/items/", { replace: true });
            dispatch(resetStatusToIdle());
        }
    }, [dispatch, navigate, loadProductFailed]);

    useEffect(() => {
        if (!(/^\d+$/.test(productId))) {
            navigate("/items/", { replace: true });
            return;
        }

        if (startDate && endDate && ((!product) || product.product_id !== productId)) {
            // Check if the from - to dates are valid and regenerate them if not before loading the product
            const startDateAsDate = DateTimeFormatConfig.getDateFromStandardPickerDateFormat(startDate);
            const endDateAsDate = DateTimeFormatConfig.getDateFromStandardPickerDateFormat(endDate);

            if (!startDateAsDate.isValid() || !endDateAsDate.isValid() || endDateAsDate.isSameOrBefore(startDateAsDate)) {
                const calculatedDates = ShopDatesInterval.get(workingHours, semesterSettings, 0, 0, startDateAsDate.isValid() ? startDate : null, endDateAsDate.isValid() ? endDate : null, closingDays);
                dispatch(setStartDate(calculatedDates.startDate));
                dispatch(setEndDate(calculatedDates.endDate));
                return;
            }
            dispatch(getProduct({ productId }));
            dispatch(getAccessories({ productId }));
            dispatch(getSimilars({ productId }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, navigate, product, productId, startDate, endDate]);

    const debouncedAvailabilityCheck = useDebounceCallback(
        (product, similarArticles, optionalArticles) => {
            const hasValidQuantity = product && ValidationUtils.isValidQuantity(product.quantity);
            if (product && !product.hasRecurrenceErrors && !product.hasFromToErrors && hasValidQuantity) {
                dispatch(checkAvailabilityForProduct());
            }

            if (Object.values(similarArticles).length > 0) {
                dispatch(checkAvailabilityForSimilars());
            }

            if (Object.values(optionalArticles).length > 0) {
                dispatch(checkAvailabilityForAccessories());
            }
        },
        // delay in ms
        Debounce.DEBOUNCE_TIMEOUT
    );

    useEffect(() => {
        debouncedAvailabilityCheck(product, similarArticles, optionalArticles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, debouncedAvailabilityCheck, triggerAvailability]);

    useEffect(() => {
        if (!showMobileBooking) {
            setBookingDrawerOpen(false);
        }
    }, [showMobileBooking]);

    const handleProductQuantityChanged = (newValue) => {
        dispatch(updateProduct({ item: product, field: "quantity", value: newValue }));
    }

    const handleDownloadAttachment = () => {
        dispatch(downloadAttachment())
    }

    const hasSubarticlesVariants = product && product.isConfigurable && Object.values(product.subarticles).length > 0;
    const hasSetComponents = product && product.is_set_article && product.set_components && product.set_components.length > 0;
    const hasTechnicalData = !product || (product.attributes && product.attributes.length > 0);
    const productDescription = product ? htmlDecode(product.description || '') : '';
    const sanitizedProductDescription = product ? DOMPurify.sanitize(productDescription) : '';
    const hasDescriptionContent = sanitizedProductDescription.replace(/<[^>]*>/g, '').replace(/\u00a0/g, ' ').trim().length > 0 || /<img\b[^>]*\bsrc\s*=\s*["']?[^"'\s>]+/i.test(sanitizedProductDescription);
    const hasArticleDescription = !product || hasDescriptionContent || (product && product.attachment);
    const showProductReservationSection = allArticlesMustBeInASingleInterval || (product && !product.isConfigurable);
    const showBookingColumn = showProductReservationSection || hasSubarticlesVariants;
    const productArticleNumber = product && (
        product.article_number ||
        product.gnr ||
        [product.prefix_or_gnr, product.number].filter(Boolean).join(' - ')
    );

    const renderBookingContent = (onClose = false) => product && product.show_as_showcase ?
        <QVContainer borderRadius={8} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Tooltip
                title={t("product.not_bookable_tooltip")}
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: 'white',
                            color: 'black',
                            padding: '10px',
                            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)'
                        }
                    }
                }}
                disableHoverListener={false}
                disableFocusListener={false}
                disableTouchListener={false}
            >
                <span>
                    <PrimaryButton label={t("product.not_bookable_in_shop")} disabled={true} />
                </span>
            </Tooltip>
        </QVContainer> :
        <ArticleReservationSection
            direction="column"
            loadingAvailability={loadingProductAvailability}
            item={product}
            onChangeQuantity={handleProductQuantityChanged}
            showReservationSection={showProductReservationSection}
            showQuantity={!(allArticlesMustBeInASingleInterval && product && product.isConfigurable)}
            showAddToCartButton={!(allArticlesMustBeInASingleInterval && product && product.isConfigurable)}
            onClose={onClose}
        >
            {hasSubarticlesVariants &&
                <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
                    <Typography variant="h6">
                        {t("productdetails.subarticles_variants")}
                    </Typography>
                    {Object.values(product.subarticles).map(subarticle =>
                        <SubarticleSection key={subarticle.id} direction="column" loadingAvailability={loadingProductAvailability} product={product} item={subarticle} />
                    )}
                </Stack>
            }
        </ArticleReservationSection>;

    return (
        <Container sx={{ ...flexColFullHeightScrollable, ...notScrollable, ...p1, pb: { xs: showBookingColumn ? 10 : 1, md: 1 } }} component="main" maxWidth={false} disableGutters>
            <Stack sx={m1} direction="column" spacing={3} alignItems="flex-start">
                <Stack direction="row" spacing={2} alignItems="center">
                    <BackButton to={"/items/" + selectedCategory} />
                    <Box sx={{
                        display: { xs: 'none', sm: 'block' }
                    }}>
                        <Breadcrumbs />
                    </Box>
                </Stack>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={3}
                    alignItems="flex-start"
                    sx={{ width: '100%' }}
                >
                    <Stack direction="column" spacing={3} sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                        <Stack
                            direction={{ xs: 'column', lg: 'row' }}
                            spacing={3}
                            alignItems={{ xs: 'flex-start', lg: 'stretch' }}
                            sx={{
                                width: '100%',
                                '--product-details-top-box-height': '420px',
                                height: { lg: 'var(--product-details-top-box-height)' }
                            }}
                        >
                            {images && images.length > 0 && <ProductImageGallery images={images} />}
                            <Stack direction="column" spacing={2} alignItems="flex-start" sx={{ flex: '1 1 auto', alignSelf: 'stretch', minWidth: 0, width: '100%' }}>
                                <Box>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Typography variant="h6" component="div">
                                        {product ? he.decode(product.name) : <Skeleton width="500px" />}
                                        </Typography>
                                        {product && product.is_set_article && <Box
                                            component="span"
                                            sx={{
                                                px: 1.5,
                                                py: 0.25,
                                                borderRadius: 999,
                                                bgcolor: '#e6e6e6',
                                                color: '#212121',
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                lineHeight: 1.6
                                            }}
                                        >
                                            SET
                                        </Box>}
                                    </Stack>
                                    {productArticleNumber && <Typography variant="body1" color="text.secondary" sx={{ opacity: 0.5 }}>
                                        Artikel-Nr.: {productArticleNumber}
                                    </Typography>}
                                </Box>

                                {!(product && product.show_as_showcase) &&
                                    <Box sx={{ width: '100%', flex: '1 1 auto', display: 'flex' }}>
                                        <Stack direction='column' sx={{ width: '100%' }}>
                                            <AvailabilityCalendar />
                                        </Stack>
                                    </Box>
                                }
                            </Stack>
                        </Stack>
                        {hasArticleDescription &&
                            <Stack direction="column" spacing={2} sx={{ width: '100%' }}>
                                <Typography variant="h6">
                                    {t("productdetails.description")}
                                </Typography>
                                <QVContainer borderRadius={8} sx={{ width: '100%', display: 'block', p: 3 }}>
                                    {product && hasDescriptionContent && <Typography variant="body1" dangerouslySetInnerHTML={{ __html: sanitizedProductDescription }} ></Typography>}
                                    {!product && <Typography variant="body1" color="text.secondary"><Skeleton /></Typography>}
                                    {product && product.attachment && <Button sx={{ mt: 2 }} variant="outlined" size="small" onClick={handleDownloadAttachment}>
                                        {t("button.downloadAttachment")}
                                    </Button>}
                                </QVContainer>
                            </Stack>
                        }
                        {((!hasSubarticlesVariants && hasTechnicalData) || hasSetComponents) &&
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={3}
                                alignItems="flex-start"
                                sx={{ width: '100%' }}
                            >
                                {(!hasSubarticlesVariants && hasTechnicalData) &&
                                    <Stack
                                        direction="column"
                                        spacing={2}
                                        sx={{ flex: '1 1 0', minWidth: 0, width: '100%' }}
                                    >
                                        <Typography variant="h6">
                                            {t("productdetails.technical_data")}
                                        </Typography>
                                        <SpecificationsBox
                                            item={product}
                                            specifications={product && product.attributes ? product.attributes : []}
                                            sx={{
                                                border: 'none',
                                                boxShadow: '0 1px 1.5px rgba(0, 0, 0, 0.11) !important'
                                            }}
                                        />
                                    </Stack>
                                }

                                {hasSetComponents &&
                                    <Stack direction='column' spacing={2} sx={{ flex: '1 1 0', minWidth: 0, width: '100%' }}>
                                        <Typography variant="h6">
                                            {t('productdetails.set_components')}
                                        </Typography>
                                        <SetComponentsBox components={product.set_components} />
                                    </Stack>
                                }
                            </Stack>
                        }
                        {optionalArticles && Object.values(optionalArticles).length > 0 &&
                            <ExtraArticles
                                title={t("optionalArticles")}
                                articles={optionalArticles}
                                loading={loadingAccessories}
                                hideAddToCartButton={(!allArticlesMustBeInASingleInterval) && product && product.isConfigurable}
                            />
                        }
                        {similarArticles && Object.values(similarArticles).length > 0 &&
                            <ExtraArticles
                                title={t("similarArticles")}
                                articles={similarArticles}
                                loading={loadingSimilars}
                                hideAddToCartButton={(!allArticlesMustBeInASingleInterval) && product && product.isConfigurable}
                            />
                        }
                    </Stack>
                    {showBookingColumn &&
                        <Box
                            sx={{
                                width: { xs: '100%', md: 360 },
                                flex: { md: '0 0 360px' },
                                display: { xs: 'none', md: 'block' }
                            }}
                        >
                            {renderBookingContent()}
                        </Box>
                    }
                </Stack>

            </Stack>
            {showBookingColumn && showMobileBooking &&
                <>
                    <Box
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: (theme) => theme.zIndex.drawer - 1,
                            display: { xs: bookingDrawerOpen ? 'none' : 'flex', md: 'none' },
                            p: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.96)',
                            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.12)'
                        }}
                    >
                        <PrimaryButton label={t("button.goToBooking")} onClick={() => setBookingDrawerOpen(true)} sx={{ width: '100%' }} />
                    </Box>
                    <Drawer
                        anchor="bottom"
                        open={bookingDrawerOpen}
                        onClose={() => setBookingDrawerOpen(false)}
                        sx={{
                            zIndex: (theme) => theme.zIndex.drawer + 2
                        }}
                        ModalProps={{
                            BackdropProps: {
                                sx: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.45)'
                                }
                            }
                        }}
                        PaperProps={{
                            sx: {
                                position: 'fixed',
                                top: 'auto',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                maxHeight: '88vh',
                                overflowY: 'auto',
                                p: 2,
                                borderTopLeftRadius: 12,
                                borderTopRightRadius: 12,
                                bgcolor: 'background.default'
                            }
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: 520, mx: 'auto', pb: 2 }}>
                            {renderBookingContent(() => setBookingDrawerOpen(false))}
                        </Box>
                    </Drawer>
                </>
            }
        </Container>
    );
};
